<template>
  <div class="list">
    <Error v-if="error" :error="error"/>
    <Loading v-if="loading"/>

    <div class="bar" v-if="items">
      <button class="filter" type="button" @click="flipOrder">
        {{$t('im.order')}} &#9656; {{$t('im.order_' + order)}}
      </button>
      <button class="filter" type="button" @click="nextFilter">
        {{$t('im.filter')}} &#9656; {{$t('im.filter_' + filter)}}
      </button>
    </div>
    <Item v-for="item in visible" :key="item.id" :item="item"/>
    <div class="empty" v-if="items && visible.length === 0">{{$t('im.nothing')}}</div>
  </div>
</template>

<script>
import Error from '../Error';
import Loading from '../Loading';

import Item from './List/Item';
import likes from '@/likes';

const FILTERS = ['off', 'liked'];

// kept for the whole visit of the site: coming back from a post shows the list at once, where it was
let cachedItems = null;
let savedTop = 0;
let savedAnchor = null;   // { id, dy }: the first post in view and how far below the top edge it was

export default {
  name: 'List',
  data() {
    let filter = 'off';
    try {
      const saved = localStorage.getItem('imFilter');
      if (FILTERS.indexOf(saved) !== -1) filter = saved;
    } catch (e) { /* no storage */ }
    let order = 'game';
    try {
      if (localStorage.getItem('imOrder2') === 'flip') order = 'flip';
    } catch (e) { /* no storage */ }
    return {
      order,
      error: null,
      loading: null,

      items: null,
      filter,
    };
  },
  components: {
    Error,
    Loading,

    Item,
  },
  computed: {
    hasHistory() {
      const seen = {};
      (this.items || []).forEach(i => { seen[i.version] = 1; });
      return Object.keys(seen).length > 1;
    },
    visible() {
      if (!this.items) return [];
      let list = this.items;
      if (this.filter === 'liked') list = list.filter(i => likes.isLiked(`im:${i.id}`));
      return this.order === 'flip' ? list.slice().reverse() : list;
    }
  },
  created () {
    this.fetchData()
  },
  watch: {
    '$route': 'fetchData'
  },
  beforeRouteLeave(to, from, next) {
    const s = this.scroller();
    if (s) {
      savedTop = s.scrollTop;
      savedAnchor = null;
      const top = s.getBoundingClientRect().top;
      const first = [...s.querySelectorAll('.item[data-id]')].find(e => e.getBoundingClientRect().bottom > top);
      if (first) savedAnchor = { id: first.dataset.id, dy: first.getBoundingClientRect().top - top };
    }
    next();
  },
  methods: {
    // the element that scrolls: the .im block of views/iM.vue
    scroller() {
      return this.$el && this.$el.closest ? this.$el.closest('.im') : null;
    },
    // back on the list: the post we came from (#id, the Back button in the header) or the old position
    restoreScroll() {
      if (this.$route.hash) return;            // List/Item.vue scrolls to the post itself
      if (!savedAnchor && !savedTop) return;   // first visit: nothing to restore
      // the person scrolls on their own: leave the position alone from then on
      let touched = false;
      const EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];
      const touch = () => { touched = true; };
      EVENTS.forEach(ev => window.addEventListener(ev, touch, true));
      setTimeout(() => EVENTS.forEach(ev => window.removeEventListener(ev, touch, true)), 1500);
      const apply = () => {
        const s = this.scroller();
        if (!s || touched) return;
        const el = savedAnchor && [...s.querySelectorAll('.item[data-id]')].find(e => e.dataset.id === savedAnchor.id);
        if (el) s.scrollTop += el.getBoundingClientRect().top - s.getBoundingClientRect().top - savedAnchor.dy;
        else s.scrollTop = savedTop;
      };
      this.$nextTick(apply);
      // again when the fonts and avatars are in: they change the heights of the posts
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.$nextTick(apply));
      setTimeout(apply, 300);
      setTimeout(apply, 1000);
    },
    flipOrder() {
      this.order = this.order === 'flip' ? 'game' : 'flip';
      try { localStorage.setItem('imOrder2', this.order); } catch (e) { /* no storage */ }
    },
    nextFilter() {
      this.filter = FILTERS[(FILTERS.indexOf(this.filter) + 1) % FILTERS.length];
      try { localStorage.setItem('imFilter', this.filter); } catch (e) { /* no storage */ }
    },
    fetchData() {
      this.error = null;
      if (cachedItems) {
        if (this.items !== cachedItems) {
          this.items = cachedItems;
          this.restoreScroll();
        }
        return;
      }
      this.items = null;
      this.loading = true;
      fetch("./data/imlist.json").then(res => {
        this.loading = false;
        if (res.ok) res.json().then(data=>{
          cachedItems = data;
          this.items = data;
          this.restoreScroll();
        }); else {
          this.error = res.status+' '+res.statusText;
        }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.bar {
  display: flex;
  align-items: center;
  .only { margin-right: auto; color: var(--accent); font-size: .95em; }
  justify-content: flex-end;
  gap: 22px;
  padding: 8px 24px 0;
  .filter {
    font-size: .95em;
    letter-spacing: .06em;
    color: var(--dim);
    &:hover {
      color: var(--accent);
    }
  }
}
.empty {
  padding: 40px 20px;
  text-align: center;
  color: var(--dim);
}
</style>
